// Workforce › Access state (WS-B): portal groups, resource access grants,
// portal listings, access requests and group membership.
//
// Lists that drive the cloud's client-side tables (search + filter over every
// row) use `useAll*` hooks, which page through the API. Every mutation here
// refetches all of the access loaders so groups, grants, listings and members
// stay in sync across pages.
import type {
  DashboardInstancePortalsAccessCreateBody,
  DashboardInstancePortalsAccessListQuery,
  DashboardInstancePortalsAccessRequestsListQuery,
  DashboardInstancePortalsAccessRequestsUpdateBody,
  DashboardInstancePortalsConsumerGroupsCreateBody,
  DashboardInstancePortalsConsumerGroupsListQuery,
  DashboardInstancePortalsConsumerGroupsUpdateBody,
  DashboardInstancePortalsConsumerProfilesListQuery,
  DashboardInstancePortalsListingsCreateBody,
  DashboardInstancePortalsListingsListQuery,
  DashboardInstancePortalsListingsUpdateBody,
  DashboardInstanceProviderTemplatesCreateBody
} from '@metorial/dashboard-sdk';
import { createLoader, useMutation } from '@metorial/data-hooks';
import {
  allPortalConsumerAccessLoader,
  portalConsumerAccessLoader,
  portalConsumerGroupsLoader,
  portalConsumerProfilesLoader
} from '../consumer/loaders/portalConsumers';
import { autoPaginate } from '../lib/autoPaginate';
import { withAuth } from '../user';

type PortalInput = { instanceId: string; portalId: string };

let MAX_ITEMS = 1000;

// ---------------------------------------------------------------------------
// Loaders

export let allPortalConsumerGroupsLoader = createLoader({
  name: 'workforceAllPortalConsumerGroups',
  fetch: (
    i: PortalInput & Omit<DashboardInstancePortalsConsumerGroupsListQuery, 'after' | 'before'>
  ) =>
    withAuth(sdk =>
      autoPaginate(
        cursor =>
          sdk.portals.consumerGroups.list(i.instanceId, i.portalId, {
            ...i,
            ...cursor,
            order: i.order ?? 'asc'
          }),
        undefined,
        MAX_ITEMS
      )
    ),
  mutators: {}
});

export let portalConsumerGroupLoader = createLoader({
  name: 'workforcePortalConsumerGroup',
  fetch: (i: PortalInput & { consumerGroupId: string }) =>
    withAuth(sdk =>
      sdk.portals.consumerGroups.get(i.instanceId, i.portalId, i.consumerGroupId)
    ),
  mutators: {}
});

export let allPortalConsumerProfilesLoader = createLoader({
  name: 'workforceAllPortalConsumerProfiles',
  fetch: (
    i: PortalInput & Omit<DashboardInstancePortalsConsumerProfilesListQuery, 'after' | 'before'>
  ) =>
    withAuth(sdk =>
      autoPaginate(
        cursor =>
          sdk.portals.consumerProfiles.list(i.instanceId, i.portalId, {
            ...i,
            ...cursor,
            order: i.order ?? 'desc'
          }),
        undefined,
        MAX_ITEMS
      )
    ),
  mutators: {}
});

export let allPortalAccessLoader = createLoader({
  name: 'workforceAllPortalAccess',
  fetch: (
    i: PortalInput & Omit<DashboardInstancePortalsAccessListQuery, 'after' | 'before' | 'cursor'>
  ) =>
    withAuth(sdk =>
      autoPaginate(
        cursor =>
          sdk.portals.consumerAccess.list(i.instanceId, i.portalId, {
            ...i,
            ...cursor,
            order: i.order ?? 'desc'
          }),
        undefined,
        MAX_ITEMS
      )
    ),
  mutators: {}
});

export let allPortalAccessListingsLoader = createLoader({
  name: 'workforceAllPortalAccessListings',
  fetch: (
    i: PortalInput & Omit<DashboardInstancePortalsListingsListQuery, 'after' | 'before' | 'cursor'>
  ) =>
    withAuth(sdk =>
      autoPaginate(
        cursor =>
          sdk.portals.consumerAccessListings.list(i.instanceId, i.portalId, {
            ...i,
            ...cursor,
            order: i.order ?? 'desc'
          }),
        undefined,
        MAX_ITEMS
      )
    ),
  mutators: {}
});

export let portalAccessListingLoader = createLoader({
  name: 'workforcePortalAccessListing',
  fetch: (i: PortalInput & { listingId: string }) =>
    withAuth(sdk =>
      sdk.portals.consumerAccessListings.get(i.instanceId, i.portalId, i.listingId)
    ),
  mutators: {}
});

export let allPortalAccessRequestsLoader = createLoader({
  name: 'workforceAllPortalAccessRequests',
  fetch: (
    i: PortalInput &
      Omit<DashboardInstancePortalsAccessRequestsListQuery, 'after' | 'before' | 'cursor'>
  ) =>
    withAuth(sdk =>
      autoPaginate(
        cursor =>
          sdk.portals.accessRequests.list(i.instanceId, i.portalId, {
            ...i,
            ...cursor,
            order: i.order ?? 'desc'
          }),
        undefined,
        MAX_ITEMS
      )
    ),
  mutators: {}
});

/** Refetch every Workforce › Access loader (after any grant / group / listing change). */
export let refetchWorkforceAccess = () => {
  allPortalConsumerGroupsLoader.refetchAll();
  portalConsumerGroupLoader.refetchAll();
  allPortalConsumerProfilesLoader.refetchAll();
  allPortalAccessLoader.refetchAll();
  allPortalAccessListingsLoader.refetchAll();
  portalAccessListingLoader.refetchAll();
  allPortalAccessRequestsLoader.refetchAll();

  // Loaders from consumer/loaders/portalConsumers.ts (marketplace managers, accounts).
  portalConsumerGroupsLoader.refetchAll();
  portalConsumerProfilesLoader.refetchAll();
  allPortalConsumerAccessLoader.refetchAll();
  portalConsumerAccessLoader.refetchAll();
};

let useAccessMutation = <I, O>(fn: (i: I) => Promise<O>, opts?: { quiet?: boolean }) =>
  useMutation(fn, { onSuccess: refetchWorkforceAccess, disableToast: opts?.quiet });

let enabled = (...values: (string | null | undefined)[]) => values.every(v => !!v);

// ---------------------------------------------------------------------------
// Groups

/** Every (non-personal) group of the portal, oldest first. */
export let useAllPortalConsumerGroups = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  query?: Omit<DashboardInstancePortalsConsumerGroupsListQuery, 'after' | 'before'>
) =>
  allPortalConsumerGroupsLoader.use(
    enabled(instanceId, portalId)
      ? { instanceId: instanceId!, portalId: portalId!, ...(query ?? {}) }
      : null
  );

export let usePortalConsumerGroup = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  consumerGroupId: string | null | undefined
) =>
  portalConsumerGroupLoader.use(
    enabled(instanceId, portalId, consumerGroupId)
      ? { instanceId: instanceId!, portalId: portalId!, consumerGroupId: consumerGroupId! }
      : null
  );

export let useCreatePortalConsumerGroup = () =>
  useAccessMutation(
    (i: PortalInput & DashboardInstancePortalsConsumerGroupsCreateBody) =>
      withAuth(sdk => sdk.portals.consumerGroups.create(i.instanceId, i.portalId, i))
  );

export let useUpdatePortalConsumerGroup = () =>
  useAccessMutation(
    (
      i: PortalInput & { consumerGroupId: string } & DashboardInstancePortalsConsumerGroupsUpdateBody
    ) =>
      withAuth(sdk =>
        sdk.portals.consumerGroups.update(i.instanceId, i.portalId, i.consumerGroupId, i)
      )
  );

export let useDeletePortalConsumerGroup = () =>
  useAccessMutation((i: PortalInput & { consumerGroupId: string }) =>
    withAuth(sdk =>
      sdk.portals.consumerGroups.delete(i.instanceId, i.portalId, i.consumerGroupId)
    )
  );

// ---------------------------------------------------------------------------
// Group members (consumer profiles)

export let useAllPortalConsumerProfiles = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  query?: Omit<DashboardInstancePortalsConsumerProfilesListQuery, 'after' | 'before'> | null
) =>
  allPortalConsumerProfilesLoader.use(
    enabled(instanceId, portalId) && query !== null
      ? { instanceId: instanceId!, portalId: portalId!, ...(query ?? {}) }
      : null
  );

export let useAssignPortalConsumerProfileGroups = () =>
  useAccessMutation(
    (i: PortalInput & { consumerProfileId: string; groupIds: string[] }) =>
      withAuth(sdk =>
        sdk.portals.consumerProfiles.assignGroups(i.instanceId, i.portalId, i.consumerProfileId, {
          groupIds: i.groupIds
        })
      ),
    { quiet: true }
  );

export let useUnassignPortalConsumerProfileGroups = () =>
  useAccessMutation(
    (i: PortalInput & { consumerProfileId: string; groupIds: string[] }) =>
      withAuth(sdk =>
        sdk.portals.consumerProfiles.unassignGroups(
          i.instanceId,
          i.portalId,
          i.consumerProfileId,
          { groupIds: i.groupIds }
        )
      ),
    { quiet: true }
  );

// ---------------------------------------------------------------------------
// Access grants (consumer.access)

export let useAllPortalAccess = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  query?: Omit<DashboardInstancePortalsAccessListQuery, 'after' | 'before' | 'cursor'> | null
) =>
  allPortalAccessLoader.use(
    enabled(instanceId, portalId) && query !== null
      ? { instanceId: instanceId!, portalId: portalId!, ...(query ?? {}) }
      : null
  );

export let useGrantPortalAccess = () =>
  useAccessMutation(
    (i: PortalInput & DashboardInstancePortalsAccessCreateBody) =>
      withAuth(sdk => sdk.portals.consumerAccess.create(i.instanceId, i.portalId, i)),
    { quiet: true }
  );

export let useRevokePortalAccess = () =>
  useAccessMutation(
    (i: PortalInput & { consumerAccessId: string }) =>
      withAuth(sdk =>
        sdk.portals.consumerAccess.delete(i.instanceId, i.portalId, i.consumerAccessId)
      ),
    { quiet: true }
  );

// ---------------------------------------------------------------------------
// Listings (consumer.access_listing): how a resource appears in the portal

export let useAllPortalAccessListings = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  query?: Omit<DashboardInstancePortalsListingsListQuery, 'after' | 'before' | 'cursor'> | null
) =>
  allPortalAccessListingsLoader.use(
    enabled(instanceId, portalId) && query !== null
      ? { instanceId: instanceId!, portalId: portalId!, ...(query ?? {}) }
      : null
  );

export let usePortalAccessListing = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  listingId: string | null | undefined
) =>
  portalAccessListingLoader.use(
    enabled(instanceId, portalId, listingId)
      ? { instanceId: instanceId!, portalId: portalId!, listingId: listingId! }
      : null
  );

export let useCreatePortalAccessListing = () =>
  useAccessMutation(
    (i: PortalInput & DashboardInstancePortalsListingsCreateBody) =>
      withAuth(sdk => sdk.portals.consumerAccessListings.create(i.instanceId, i.portalId, i)),
    { quiet: true }
  );

export let useUpdatePortalAccessListing = () =>
  useAccessMutation(
    (i: PortalInput & { listingId: string } & DashboardInstancePortalsListingsUpdateBody) =>
      withAuth(sdk =>
        sdk.portals.consumerAccessListings.update(i.instanceId, i.portalId, i.listingId, {
          name: i.name,
          description: i.description,
          readme: i.readme
        })
      )
  );

export let useDeletePortalAccessListing = () =>
  useAccessMutation((i: PortalInput & { listingId: string }) =>
    withAuth(sdk =>
      sdk.portals.consumerAccessListings.delete(i.instanceId, i.portalId, i.listingId)
    )
  );

// ---------------------------------------------------------------------------
// Access requests

export let useAllPortalAccessRequests = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined,
  query?: Omit<
    DashboardInstancePortalsAccessRequestsListQuery,
    'after' | 'before' | 'cursor'
  > | null
) =>
  allPortalAccessRequestsLoader.use(
    enabled(instanceId, portalId) && query !== null
      ? { instanceId: instanceId!, portalId: portalId!, ...(query ?? {}) }
      : null
  );

export let useReviewPortalAccessRequest = () =>
  useAccessMutation(
    (
      i: PortalInput & {
        accessRequestId: string;
      } & DashboardInstancePortalsAccessRequestsUpdateBody
    ) =>
      withAuth(sdk =>
        sdk.portals.accessRequests.update(i.instanceId, i.portalId, i.accessRequestId, {
          status: i.status,
          resolutionMessage: i.resolutionMessage,
          consumerGroupId: i.consumerGroupId
        })
      )
  );

// ---------------------------------------------------------------------------
// Provider templates (the per-portal wrapper of an integration)

/** Idempotent: the API returns the integration's existing active template if there is one. */
export let useCreatePortalProviderTemplate = () =>
  useMutation(
    (i: { instanceId: string } & DashboardInstanceProviderTemplatesCreateBody) =>
      withAuth(sdk => sdk.providerTemplates.create(i.instanceId, i)),
    { disableToast: true }
  );
